import type { ComponentType, CSSProperties, ReactNode } from 'react';
import {
  House, Plug, Car, ShoppingCart, HeartPulse, Repeat, Package,
  PiggyBank, Landmark, ChartLine, Target, ShieldCheck, Sparkles,
} from '@vitness/icons';
import type { CspBucket } from '@shared/types';

/** Mono uppercase card label — matches the design system's eyebrow rhythm. */
export function SectionLabel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{
      fontFamily: 'var(--vn-font-mono)', fontSize: 9, fontWeight: 500,
      textTransform: 'uppercase', letterSpacing: '0.1em',
      color: 'var(--vn-text-muted)', marginBottom: 12, ...style,
    }}>
      {children}
    </div>
  );
}

/** Inset explanatory note. Used where a number needs a sentence. */
export function Note({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'good' | 'warn' | 'bad' }) {
  const tones = {
    neutral: { bg: 'var(--vn-surface-inset)', bd: 'var(--vn-border-default)' },
    good: { bg: 'rgba(34,197,94,0.07)', bd: 'rgba(34,197,94,0.25)' },
    warn: { bg: 'rgba(245,158,11,0.07)', bd: 'rgba(245,158,11,0.25)' },
    bad: { bg: 'rgba(239,68,68,0.07)', bd: 'rgba(239,68,68,0.25)' },
  }[tone];
  return (
    <div style={{
      marginTop: 12, padding: '10px 12px', borderRadius: 'var(--vn-radius-md)',
      background: tones.bg, border: `1px solid ${tones.bd}`,
      fontFamily: 'var(--vn-font-mono)', fontSize: 10.5, lineHeight: 1.6,
      color: 'var(--vn-text-secondary)',
    }}>
      {children}
    </div>
  );
}

/**
 * Bucket colours. Invest is teal, not green, on purpose: green already means
 * three other things here (brand accent, "surplus", active status), and a
 * green Investments figure read as "good" rather than "investments". Semantic
 * green/red stays reserved for money direction.
 */
export function bucketColour(b: CspBucket | null): string {
  if (!b) return 'var(--vn-text-muted)'; // unmapped — deliberately colourless
  return {
    fixed: 'var(--vn-info)',
    invest: '#14b8a6',
    savings: 'var(--vn-accent-violet)',
    gfs: '#f472b6',
  }[b];
}

export const BUCKET_LABEL: Record<CspBucket, string> = {
  fixed: 'Fixed', invest: 'Investments', savings: 'Savings', gfs: 'Guilt-Free',
};

export function Dot({ color, size = 8 }: { color: string; size?: number }) {
  return <span style={{ width: size, height: size, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />;
}

/**
 * Group → Lucide icon. Preferred over the Notion emoji where a mapping exists:
 * emoji render differently on every platform and clash with the line icons the
 * rest of the chrome uses. The emoji stays as fallback for anything unmapped,
 * so a new Notion category degrades to charming rather than blank.
 */
const GROUP_ICON: Record<string, ComponentType<{ size?: number; color?: string }>> = {
  housing: House,
  utilities: Plug,
  auto: Car,
  groceries: ShoppingCart,
  health: HeartPulse,
  subs: Repeat,
  misc: Package,
  k401: PiggyBank,
  roth: Landmark,
  brokerage: ChartLine,
  savingsGoals: Target,
  emergency: ShieldCheck,
  gfs: Sparkles,
};

/**
 * A category's icon: mapped Lucide glyph first, then the Notion page icon
 * (emoji as the character, uploaded/linked images as a URL → <img>).
 */
export function CategoryIcon({ icon, groupKey, size = 13 }: {
  icon?: string | null; groupKey?: string; size?: number;
}) {
  const Mapped = groupKey ? GROUP_ICON[groupKey] : undefined;
  if (Mapped) {
    return <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--vn-text-muted)' }}><Mapped size={size} /></span>;
  }
  if (!icon) return null;
  if (/^https?:\/\//.test(icon)) {
    return <img src={icon} alt="" width={size} height={size} style={{ borderRadius: 3, flexShrink: 0 }} />;
  }
  return <span aria-hidden style={{ fontSize: size, lineHeight: 1, flexShrink: 0 }}>{icon}</span>;
}

/** Responsive auto-fit grid. min sets the smallest comfortable column. */
export const autoGrid = (min = 190): CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
  gap: 'var(--vn-density-grid-gap)',
});

/**
 * Fixed 2×2 for quartets of related figures. Auto-fit puts four stats in one
 * long row on desktop and a single column on a phone; both lose the pairing
 * that makes them scannable (net vs committed, unallocated vs tax). Two by
 * two keeps each pair on its own row at every width.
 */
export const pairGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--vn-density-grid-gap)',
};

/** Numeric cell — tabular figures so columns don't jitter while dragging. */
export function Num({ children, style, title, className }: {
  children: ReactNode; style?: CSSProperties; title?: string; className?: string;
}) {
  return <span className={className ? `vd-num ${className}` : 'vd-num'} style={style} title={title}>{children}</span>;
}

export function KeyVal({ label, value, color }: { label: string; value: ReactNode; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--vn-font-mono)', fontSize: 11.5, padding: '3px 0' }}>
      <span style={{ color: 'var(--vn-text-muted)' }}>{label}</span>
      <span style={{ color: color ?? 'var(--vn-text-primary)' }}>{value}</span>
    </div>
  );
}
